import React, { useState, useEffect } from 'react';
import './main.css';

function SpinningLoader() {
  const [spinnerIndex, setSpinnerIndex] = useState(0);
  const spinnerChars = ['/', '|', '\\', '-'];

  useEffect(() => {
    const interval = setInterval(() => {
      setSpinnerIndex((prev) => (prev + 1) % spinnerChars.length);
    }, 200);

    return () => clearInterval(interval);
  }, []);

  return (
    <span style={{ 
      backgroundColor: '#1a1a1a', 
      color: 'white', 
      padding: '2px 6px',
      fontFamily: 'monospace',
      borderRadius: '2px',
      fontSize: '12px'
    }}>
      {spinnerChars[spinnerIndex]}
    </span>
  );
}

function Box({ message, className, onClick, style }: { message: string | React.ReactNode, className: string, onClick?: () => void, style?: React.CSSProperties }) {
  const [displayText, setDisplayText] = useState(message);
  const [isGlitching, setIsGlitching] = useState(false);

  const glitchChars = '#$%&@';
  const originalText = typeof message === 'string' ? message : '';

  useEffect(() => {
    if (isGlitching && typeof message === 'string') {
      let iteration = 0;
      const interval = setInterval(() => {
        setDisplayText((prev) => {
          return originalText
            .split('')
            .map((letter, index) => {
              if (index < iteration) {
                return originalText[index];
              }
              return glitchChars[Math.floor(Math.random() * glitchChars.length)];
            })
            .join('');
        });

        if (iteration >= originalText.length) {
          clearInterval(interval);
          setDisplayText(originalText);
          setIsGlitching(false);
        }

        iteration += 1 / 3;
      }, 30);

      return () => clearInterval(interval);
    }
  }, [isGlitching, originalText]);

  const handleMouseEnter = () => {
    if (typeof message === 'string' && onClick) {
      setIsGlitching(true);
    }
  };

  const handleMouseLeave = () => {
    setDisplayText(originalText);
  };

  return (
    <div 
      className={className} 
      onClick={onClick} 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ ...(onClick ? { cursor: 'pointer' } : {}), ...style }}
    >
      {typeof message === 'string' ? displayText : message}
    </div>
  );
}

function SlowEmojiEffect() {
  const [emojiIndex, setEmojiIndex] = useState(0);
  const relevantEmojis = ['🎬', '🎨', '✨', '🎭', '🎪', '💫', '🌟', '🎯', '🎲', '🎊'];

  useEffect(() => {
    const interval = setInterval(() => {
      setEmojiIndex((prev) => (prev + 1) % relevantEmojis.length);
    }, 800); // Slower than bracket emoji

    return () => clearInterval(interval);
  }, []);

  return (
    <span style={{ 
      backgroundColor: '#1a1a1a', 
      color: 'white', 
      padding: '2px 6px',
      fontFamily: 'monospace',
      borderRadius: '2px',
      fontSize: '14px',
      margin: '0 4px'
    }}>
      {relevantEmojis[emojiIndex]}
    </span>
  );
}

function SimpleLink({ href, children }: { href: string, children: React.ReactNode }) {
  return (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      style={{ 
        color: '#1a1a1a', 
        textDecoration: 'none',
        display: 'block'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#1a1a1a';
        e.currentTarget.style.color = 'white';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = '#1a1a1a';
      }}
    >
      {children}
    </a>
  );
}

function CyclingImages() {
  const [imageIndex, setImageIndex] = useState(0);
  const images = ['1.png', '2.png', '3.png', '4.png', '5.png', '6.png', '7.png'];

  useEffect(() => {
    const interval = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % images.length);
    }, 1500); // 1.5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <img 
      src={`/${images[imageIndex]}`} 
      alt="Cycling Images" 
      style={{ 
        width: '100%', 
        height: '100%', 
        objectFit: 'cover',
        borderRadius: '0'
      }}
    />
  );
}

function App() {
  const [activeTab, setActiveTab] = useState<string>('motion');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contentData = {
    motion: (
      <iframe
        src="https://player.vimeo.com/video/1095187309"
        width="100%"
        height="100%"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title="Craig SR Showreel"
      />
    ),
    publishing: (
      <div style={{ 
        textAlign: 'left', 
        lineHeight: '1.6', 
        fontSize: '14px',
        width: '100%',
        height: '100%',
        overflowY: 'auto'
      }}>
        <div style={{ marginBottom: '1rem', fontWeight: 'bold', textAlign: 'center' }}>Publications 💻 💻 💻 💻 💻</div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
          gap: '1rem',
          alignItems: 'start'
        }}>
          <div>
            <div style={{ marginBottom: '0.8rem' }}>
              <SimpleLink href="https://the-editorialmagazine.com/john-carroll-kirby/">
                Editorial Magazine - John Carroll Kirby "Cash for Your Trash"
              </SimpleLink>
            </div>
            
            <div style={{ marginBottom: '0.8rem' }}>
              <SimpleLink href="https://www.sugarglider.digital/blog/craig-sr">
                Sugar Glider Digital - Artist Profile: Craig S-R
              </SimpleLink>
            </div>
            
            <div style={{ marginBottom: '0.8rem' }}>
              <SimpleLink href="https://www.artshub.com.au/news/features/towards-continuum-behind-the-hardware-of-digital-art-2682166/">
                Arts Hub - "Towards continuum: behind the hardware of digital art"
              </SimpleLink>
            </div>
            
            <div style={{ marginBottom: '0.8rem' }}>
              <SimpleLink href="https://heckler.tv/collaborations/heckler-joins-forces-with-chris-yee-and-saara138-on-projected-art-installation">
                HECKLER - CHRIS YEE AND SAARA138 INSTALLATION
              </SimpleLink>
            </div>
            
            <div style={{ marginBottom: '0.8rem' }}>
              <SimpleLink href="https://beat.com.au/beats-by-beat-poetry-ravers-jobfit-provide-dance-music-from-across-the-spectrum/">
                Beat Magazine - Jobfit
              </SimpleLink>
            </div>
            
            <div style={{ marginBottom: '0.8rem' }}>
              <SimpleLink href="https://www.nme.com/en_au/news/music/shady-nasty-new-ep-clubsmoke-interview-3064681">
                NME - Shady Nasty 'PRETTYB0YZ'
              </SimpleLink>
            </div>
            
            <div style={{ marginBottom: '0.8rem' }}>
              <SimpleLink href="https://www.nme.com/en_au/news/music/shady-nasty-announces-new-ep-clubsmoke-release-new-single-3017148">
                NME - Shady Nasty 'R0LLIN' H1LLZ'
              </SimpleLink>
            </div>
            
            {isMobile && (
              <>
                <div style={{ marginBottom: '0.8rem' }}>
                  <SimpleLink href="https://stream.sydneyoperahouse.com/videos/shortwave-jobfit-craig-stubbs-race-mitifb-sorrynotsorry">
                    Sydney Opera House - Shortwave: jobfit & Craig Stubbs-Race
                  </SimpleLink>
                </div>
                
                <div style={{ marginBottom: '0.8rem' }}>
                  <SimpleLink href="https://4a.com.au/digital/optic-marcus-whale-craig-stubbs-race">
                    4A Centre for Contemporary Asian Art - 'Optic'
                  </SimpleLink>
                </div>
                
                <div style={{ marginBottom: '0.8rem' }}>
                  <SimpleLink href="https://2020.emergingwritersfestival.org.au/event/musculi-immoderata/">
                    Emerging Writers' Festival - Jamie Marina Lau 'musculi immoderata'
                  </SimpleLink>
                </div>
                
                <div style={{ marginBottom: '0.8rem' }}>
                  <SimpleLink href="https://www.nme.com/en_au/news/music/marcus-whale-shares-new-single-a-ghost-2645915">
                    NME - Marcus Whale 'A Ghost'
                  </SimpleLink>
                </div>
                
                <div style={{ marginBottom: '0.8rem' }}>
                  <SimpleLink href="https://futuretao.lololol.net/Inner_ear_rainbowchan">
                    lololol.net Future Tao - '條款 Terms'
                  </SimpleLink>
                </div>
                
                <div style={{ marginBottom: '0.8rem' }}>
                  <SimpleLink href="https://ra.co/news/69162">
                    Resident Advisor - '條款 Terms'
                  </SimpleLink>
                </div>
              </>
            )}
          </div>
          
          {!isMobile && (
            <div>
              <div style={{ marginBottom: '0.8rem' }}>
                <SimpleLink href="https://stream.sydneyoperahouse.com/videos/shortwave-jobfit-craig-stubbs-race-mitifb-sorrynotsorry">
                  Sydney Opera House - Shortwave: jobfit & Craig Stubbs-Race
                </SimpleLink>
              </div>
              
              <div style={{ marginBottom: '0.8rem' }}>
                <SimpleLink href="https://4a.com.au/digital/optic-marcus-whale-craig-stubbs-race">
                  4A Centre for Contemporary Asian Art - 'Optic'
                </SimpleLink>
              </div>
              
              <div style={{ marginBottom: '0.8rem' }}>
                <SimpleLink href="https://2020.emergingwritersfestival.org.au/event/musculi-immoderata/">
                  Emerging Writers' Festival - Jamie Marina Lau 'musculi immoderata'
                </SimpleLink>
              </div>
              
              <div style={{ marginBottom: '0.8rem' }}>
                <SimpleLink href="https://www.nme.com/en_au/news/music/marcus-whale-shares-new-single-a-ghost-2645915">
                  NME - Marcus Whale 'A Ghost'
                </SimpleLink>
              </div>
              
              <div style={{ marginBottom: '0.8rem' }}>
                <SimpleLink href="https://futuretao.lololol.net/Inner_ear_rainbowchan">
                  lololol.net Future Tao - '條款 Terms'
                </SimpleLink>
              </div>
              
              <div style={{ marginBottom: '0.8rem' }}>
                <SimpleLink href="https://ra.co/news/69162">
                  Resident Advisor - '條款 Terms'
                </SimpleLink>
              </div>
            </div>
          )}
        </div>
      </div>
    ),
    contact: (
      <div style={{ 
        textAlign: 'left', 
        lineHeight: '1.6', 
        fontSize: '14px',
        width: '100%',
        height: '100%',
        overflowY: 'auto'
      }}>
        <div style={{ marginBottom: '1rem', fontWeight: 'bold', textAlign: 'center' }}>Contact 📧 📧 📧 📧 📧</div>
        
        <div style={{ marginBottom: '0.8rem' }}>
          <SimpleLink href="mailto:hi@craigsr.com">
            hi@craigsr.com
          </SimpleLink>
        </div>
        
        <div style={{ marginBottom: '0.8rem' }}>
          <SimpleLink href="https://www.linkedin.com/in/craig-stubbs-race-2ab8b0a8/">
            LinkedIn Profile
          </SimpleLink>
        </div>
      </div>
    )
  };

  // Set initial content to motion
  const activeContent = contentData[activeTab as keyof typeof contentData];

  return (
    <div className="container">
      <div className="grid-header">
        <Box message={
          <div>
            <SpinningLoader /> Hey I'm Craig SR <SpinningLoader /> a digital designer who specialises in <SlowEmojiEffect /> 3D and motion <SlowEmojiEffect />
            <br /><br />
            I can help you bring your brand to life with slick graphics and engaging design
            <br /><br />
            Let's work together
          </div>
        } className="box-header" />
        <Box message={
          <CyclingImages />
        } className="box-header2" />
      </div>
      
      <div className="grid-nav">
        <Box 
          message="Showreel" 
          className={activeTab === 'motion' ? 'box-nav-active' : 'box-nav'}
          onClick={() => {
            setActiveTab('motion');
          }}
        />
        <Box 
          message="Publications" 
          className={activeTab === 'publishing' ? 'box-nav-active' : 'box-nav'}
          onClick={() => {
            setActiveTab('publishing');
          }}
        />
        <Box 
          message="Contact" 
          className={activeTab === 'contact' ? 'box-nav-active' : 'box-nav'}
          onClick={() => {
            setActiveTab('contact');
          }}
        />
      </div>

      <div className="grid-content">
        <Box message={activeContent} className="box-content" />
      </div>
    </div>
  );
}

export default App;
